import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Building2, Copy, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import axiosInstance from '../api/axios';
import { formatEUR } from '../utils/format';

const BankTransferSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const order_id = searchParams.get('order_id');
  const [orderData, setOrderData] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [copiedField, setCopiedField] = useState('');
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (order_id) fetchPaymentMethodDetails();
    else setLoadError(true);
    // eslint-disable-next-line
  }, [order_id]);

  const fetchPaymentMethodDetails = async () => {
    try {
      // Use order-bound endpoint (Hybrid: details only visible after purchase)
      const response = await axiosInstance.get(`/bank-details/${order_id}`);
      setOrderData({ order_number: response.data.order_id, total: response.data.total });
      setPaymentMethod({
        id: 'bank_transfer',
        name: 'Banküberweisung',
        bank_details: {
          bank_name: response.data.bank_name,
          account_holder: response.data.account_holder,
          iban: response.data.iban,
          bic: response.data.bic,
          instructions: response.data.instructions,
        },
      });
    } catch (error) {
      console.error('Error fetching bank details:', error);
      setLoadError(true);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(''), 2000);
  };

  if (!orderData || !paymentMethod) {
    return (
      <div className="min-h-screen py-16" style={{ backgroundColor: '#E8E0D5' }}>
        <div className="max-w-2xl mx-auto px-4">
          <Card>
            <CardContent className="py-12 text-center">
              {loadError ? (
                <p className="text-gray-600" data-testid="bank-transfer-load-error">
                  Ihre Bestellung ist eingegangen. Die Bankdaten finden Sie auch in Ihrer Bestätigungs-E-Mail.
                </p>
              ) : (
                <p className="text-gray-600">Lädt Bestelldetails...</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const bankDetails = paymentMethod.bank_details;

  return (
    <div className="min-h-screen py-16" style={{ backgroundColor: '#E8E0D5' }}>
      <div className="max-w-3xl mx-auto px-4">
        <Card>
          <CardHeader>
            <div className="text-center">
              <CheckCircle className="h-16 w-16 mx-auto text-green-600 mb-4" />
              <CardTitle className="text-2xl">Bestellung erfolgreich aufgegeben!</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Order Info */}
            <div className="text-center pb-6 border-b">
              <p className="text-gray-600 mb-2">
                Vielen Dank für Ihre Bestellung! Ihre Bestellnummer ist:
              </p>
              <p className="text-xl font-bold text-gray-900">
                {orderData.order_number}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Gesamtbetrag: {formatEUR(Number(orderData.total || 0))}
              </p>
            </div>

            {/* Bank Transfer Instructions */}
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-gray-900">
                <Building2 className="h-6 w-6 text-blue-600" />
                <h3 className="text-lg font-semibold">Zahlungsanweisung</h3>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 space-y-4">
                <p className="text-sm text-gray-700">
                  Bitte überweisen Sie den Betrag auf folgendes Konto:
                </p>

                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-gray-600 font-medium">Bankname</label>
                    <div className="flex items-center justify-between bg-white p-3 rounded border mt-1">
                      <span className="font-medium">{bankDetails.bank_name}</span>
                      <button
                        onClick={() => copyToClipboard(bankDetails.bank_name, 'bank')}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        {copiedField === 'bank' ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs text-gray-600 font-medium">Kontoinhaber</label>
                    <div className="flex items-center justify-between bg-white p-3 rounded border mt-1">
                      <span className="font-medium">{bankDetails.account_holder}</span>
                      <button
                        onClick={() => copyToClipboard(bankDetails.account_holder, 'holder')}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        {copiedField === 'holder' ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs text-gray-600 font-medium">IBAN</label>
                    <div className="flex items-center justify-between bg-white p-3 rounded border mt-1">
                      <span className="font-mono font-medium">{bankDetails.iban}</span>
                      <button
                        onClick={() => copyToClipboard(bankDetails.iban, 'iban')}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        {copiedField === 'iban' ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs text-gray-600 font-medium">BIC/SWIFT</label>
                    <div className="flex items-center justify-between bg-white p-3 rounded border mt-1">
                      <span className="font-mono font-medium">{bankDetails.bic}</span>
                      <button
                        onClick={() => copyToClipboard(bankDetails.bic, 'bic')}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        {copiedField === 'bic' ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs text-gray-600 font-medium">Betrag</label>
                    <div className="flex items-center justify-between bg-white p-3 rounded border mt-1">
                      <span className="font-bold text-lg">{formatEUR(Number(orderData.total || 0))}</span>
                      <button
                        onClick={() => copyToClipboard(Number(orderData.total || 0).toFixed(2), 'amount')}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        {copiedField === 'amount' ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs text-gray-600 font-medium">Verwendungszweck</label>
                    <div className="flex items-center justify-between bg-white p-3 rounded border mt-1">
                      <span className="font-medium">Bestellung {orderData.order_number}</span>
                      <button
                        onClick={() => copyToClipboard(`Bestellung ${orderData.order_number}`, 'reference')}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        {copiedField === 'reference' ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                {bankDetails.instructions && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded p-4 mt-4">
                    <p className="text-sm text-gray-700">
                      <strong>Wichtig:</strong> {bankDetails.instructions}
                    </p>
                  </div>
                )}

                <div className="bg-gray-50 rounded p-4 mt-4">
                  <p className="text-xs text-gray-600">
                    💡 <strong>Tipp:</strong> Verwenden Sie die Kopierfunktion (
                    <Copy className="h-3 w-3 inline mx-1" />
                    ), um die Daten in Ihre Banking-App zu übertragen.
                  </p>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  ⏱️ Ihre Bestellung wird nach Zahlungseingang bearbeitet. 
                  Dies kann 1-3 Werktage dauern.
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3 pt-6 border-t">
              <Button 
                onClick={() => navigate('/orders')}
                className="w-full bg-gradient-to-r from-[#C4A084] to-[#B89968] text-white"
              >
                Meine Bestellungen ansehen
              </Button>
              <Button 
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full"
              >
                Zurück zur Startseite
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BankTransferSuccess;
